/**
 * GuestEventsTable Component
 *
 * Read-only events table for desktop split-view.
 * Features:
 * - Displays all events in tabular format
 * - Click row to navigate to that event in left panel
 * - Highlights currently selected event
 * - No editing capabilities (read-only)
 */

import React, { useRef, useEffect } from 'react';
import { Calendar, Clock, MapPin, Ticket } from 'lucide-react';
import type { HMCEvent } from '../hmcEventsStore';
import { getTicketsRemaining, getCleanEventName } from '../utils/attendanceParser';
import { detectEventType } from '../utils/eventTypeDetector';
import { formatEventDateShort } from '../utils/dateUtils';

interface GuestEventsTableProps {
  events: HMCEvent[];
  currentEventIndex: number;
  onEventSelect: (index: number) => void;
  maxGuestTickets: number;
  /** Total events count before filtering (for "showing X of Y" display) */
  totalEventsCount?: number;
}

/**
 * Format date for table display
 * Uses centralized parseLocalDate to avoid timezone bugs
 */
const formatTableDate = (dateStr: string): string => {
  return formatEventDateShort(dateStr);
};

export const GuestEventsTable: React.FC<GuestEventsTableProps> = ({
  events,
  currentEventIndex,
  onEventSelect,
  maxGuestTickets,
  totalEventsCount,
}) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const selectedRowRef = useRef<HTMLTableRowElement>(null);

  // Auto-scroll to selected row when index changes
  useEffect(() => {
    if (selectedRowRef.current && tableRef.current) {
      const row = selectedRowRef.current;
      const container = tableRef.current;

      const rowTop = row.offsetTop;
      const rowBottom = rowTop + row.offsetHeight;
      const containerScrollTop = container.scrollTop;
      const containerHeight = container.clientHeight;

      // Check if row is out of view
      if (rowTop < containerScrollTop || rowBottom > containerScrollTop + containerHeight) {
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentEventIndex]);

  if (events.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-dark-muted">
        <p>No events available</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-dark-border/30 bg-dark-surface/50">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Calendar className="w-5 h-5 text-brand-400" />
          {totalEventsCount && totalEventsCount !== events.length ? (
            <>Showing {events.length} of {totalEventsCount}</>
          ) : (
            <>All Events ({events.length})</>
          )}
        </h2>
        <p className="text-sm text-dark-muted mt-1">
          Click any row to view details
        </p>
      </div>

      {/* Table container */}
      <div ref={tableRef} className="flex-1 overflow-y-auto">
        <table className="hmc-modern-table hmc-guest-table w-full">
          <tbody>
            {events.map((event, index) => {
              const isSelected = index === currentEventIndex;
              const ticketInfo = getTicketsRemaining(
                event.special_event,
                event.opponent,
                event.event_type,
                event.venue
              );
              const displayRemaining = Math.min(ticketInfo.remaining, maxGuestTickets);
              const isSoldOut = ticketInfo.remaining === 0;
              const eventName = getCleanEventName(event.opponent) || 'Event';
              const typeResult = detectEventType(event);
              const eventTime = event.event_start_time || event.kickoff_time || 'TBD';

              return (
                <tr
                  key={event.id}
                  ref={isSelected ? selectedRowRef : null}
                  onClick={() => onEventSelect(index)}
                  className={`
                    hmc-tr cursor-pointer transition-all duration-150 border-b border-dark-border/30
                    ${isSelected
                      ? 'hmc-tr-current bg-brand-500/10 border-l-4 border-l-brand-500'
                      : 'hover:bg-dark-surface/50'
                    }
                  `}
                >
                  {/* Date Column */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex flex-col">
                      <span className={`text-sm font-medium ${isSelected ? 'text-brand-300' : 'text-white'}`}>
                        {formatTableDate(event.event_date)}
                      </span>
                      <span className="text-xs text-dark-muted flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {eventTime}
                      </span>
                    </div>
                  </td>

                  {/* Event Column */}
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <span className={`text-sm font-medium truncate max-w-[200px] ${isSelected ? 'text-brand-300' : 'text-white'}`}>
                        {eventName}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded w-fit ${typeResult.tailwindClasses}`}>
                        {typeResult.label}
                      </span>
                    </div>
                  </td>

                  {/* Venue Column (hidden on smaller screens) */}
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <span className="text-sm text-dark-text flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-dark-muted" />
                      <span className="truncate max-w-[150px]">{event.venue || 'Toyota Center'}</span>
                    </span>
                  </td>

                  {/* Tickets Column */}
                  <td className="px-4 py-3 text-right">
                    <span
                      className={`
                        inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium
                        ${isSoldOut
                          ? event.event_type === 'dynamo'
                            ? 'bg-orange-500/20 text-orange-400'
                            : 'bg-amber-500/20 text-amber-400'
                          : displayRemaining <= 2
                          ? event.event_type === 'dynamo'
                            ? 'bg-orange-500/20 text-orange-400'
                            : 'bg-amber-500/20 text-amber-400'
                          : 'bg-green-500/20 text-green-400'
                        }
                      `}
                    >
                      <Ticket className="w-3.5 h-3.5" />
                      {isSoldOut ? 'Standby Available' : 'Available'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GuestEventsTable;
